import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import {
  DrawerProvider,
  ModalProvider,
  ThemeProvider
} from 'stubben-edge-unified-frontend-react-components';
import { lazy } from 'react';

import Protected from './components/common/Protected';
import FeatureProtected from './components/common/Protected/FeatureProtected';
import SavingsProtected from './components/common/Protected/SavingsProtected';
import RouteDataProvider from './store/context/RouteDataContext';
import StoreProvider from './store/context/StoreProvider';
import { AppShellSuspense } from './components/common/skeletons';
import LoginPage from './pages/auth/LoginJourney';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { allowAllPermissions } from './constants/permissions';
import ExternalRedirect from './components/common/ExternalRedirect';

import api from '@api-client/client';
import { ApiClientContext } from '@api-client/context';
import { TwilioProvider } from './components/common/TwilioProvider';

import WebviewInvoice from './pages/invoices/webview';
import ProvidersPage from './pages/providers';
import PipelineProvider from './store/context/PipelineProvider';
import { callDetailsRoutes } from './utils/callDetailsRoutes';
import { PermissionType } from './constants/types';
import LogoutPage from './pages/auth/Logout/index.tsx';
import CampaignsPage from './pages/campaigns/index.tsx';

// Lazy load all page components
const TemplatesPage = lazy(() => import('./pages/templates'));
const CampaignViewPage = lazy(
  () => import('./pages/campaigns/[id].tsx/index.tsx')
);
const FeeStructuresPage = lazy(() => import('./pages/fee-structures'));
const FeeStructureDetailPage = lazy(
  () => import('./pages/fee-structures/[id].tsx')
);
const AudiencesPage = lazy(() => import('./pages/audiences/index.tsx'));
const AudiencesPresetsPage = lazy(() => import('./pages/audiences/presets.tsx'));
const AudiencesPresetDetailPage = lazy(
  () => import('./pages/audiences/presets/[presetId].tsx')
);
const AudiencesArchivedPage = lazy(
  () => import('./pages/audiences/archived.tsx')
);
const AudiencesViewPage = lazy(
  () => import('./pages/audiences/[id].tsx/index.tsx')
);
const AudiencesUploadPage = lazy(() => import('./pages/audiences/upload.tsx'));
const AudiencesBuilderPage = lazy(() => import('./pages/audiences/builder.tsx'));
const AudiencesManualBuilderPage = lazy(
  () => import('./pages/audiences/manual-builder.tsx')
);

const CookiePolicyPage = lazy(() => import('./pages/cookie-policy'));
const NotFoundPage = lazy(() => import('./pages/404'));
const LandingPage = lazy(() => import('./pages/landing'));
const BdxImport = lazy(() => import('./pages/bdx-import'));
const DashboardPage = lazy(() => import('./pages/dashboard'));
const ObjectivesPage = lazy(() => import('./pages/objectives'));
const DataCompletenessScorePage = lazy(
  () => import('./pages/data-completeness-score')
);
const DataPlaypenPage = lazy(() => import('./pages/data-playpen'));
const DatatableShowcase = lazy(() => import('./pages/datatable-showcase'));
const MarketNewsPage = lazy(() => import('./pages/market-news'));
const MarketValuationPage = lazy(() => import('./pages/market-valuation'));
const PipelinesPage = lazy(() => import('./pages/pipelines'));
const PipelineViewPage = lazy(() => import('./pages/pipelines/[id].tsx'));
const PipelineDealImportPage = lazy(() => import('./pages/pipelines/import'));
const PipelineTaskImportPage = lazy(
  () => import('./pages/pipelines/tasks-import')
);
const PoliciesPage = lazy(() => import('./pages/policies'));
const PoliciesViewPage = lazy(() => import('./pages/policies/[id].tsx'));
const TasksViewPage = lazy(() => import('./pages/tasks/[id]'));
const AkoniCustomersPage = lazy(() => import('./pages/akoni/customers'));
const AkoniCustomerDetailPage = lazy(
  () => import('./pages/akoni/customers/[customerId]')
);
const AkoniIntroducersPage = lazy(() => import('./pages/akoni/introducers'));
const AkoniCustomerAccountDetailPage = lazy(
  () => import('./pages/akoni/customers/[customerId]/accounts/[accountId]')
);
const AkoniCustomerHubTransactionsPage = lazy(
  () => import('./pages/akoni/customers/[customerId]/hub-transactions')
);
const AkoniIndividualsPage = lazy(() => import('./pages/akoni/individuals'));
const AkoniIndividualDetailPage = lazy(
  () => import('./pages/akoni/individuals/[id].tsx')
);
const AkoniApplicationsPage = lazy(() => import('./pages/akoni/applications'));
const AkoniApplicationDetailPage = lazy(
  () => import('./pages/akoni/applications/[id].tsx')
);
const AkoniDepositInstructionsPage = lazy(
  () => import('./pages/akoni/deposit-instructions')
);
const AkoniDepositInstructionDetailPage = lazy(
  () => import('./pages/akoni/deposit-instructions/[id].tsx')
);
const AkoniDepositsPage = lazy(() => import('./pages/akoni/deposits'));
const AkoniDepositDetailPage = lazy(
  () => import('./pages/akoni/deposits/[id].tsx')
);
const AkoniTransactionsPage = lazy(() => import('./pages/akoni/transactions'));
const AkoniTransactionDetailPage = lazy(
  () => import('./pages/akoni/transactions/[id]')
);
const AkoniBanksPage = lazy(() => import('./pages/akoni/banks'));
const AkoniBankDetailPage = lazy(() => import('./pages/akoni/banks/[id]'));
const AkoniBankDepositInstructionsPage = lazy(
  () => import('./pages/akoni/banks/[id]/deposit-instructions')
);
const AkoniProductsPage = lazy(() => import('./pages/akoni/products'));
const AkoniProductDetailPage = lazy(
  () => import('./pages/akoni/products/[id]')
);
const AkoniDirectBillingReportsPage = lazy(
  () => import('./pages/akoni/direct-billing-reports')
);
const SavingsHomeDashboardPage = lazy(() => import('./pages/akoni/dashboard'));

const WorkflowsPage = lazy(() => import('./pages/workflows'));
const WorkflowEditPage = lazy(() => import('./pages/workflows/[id]'));
const WorkflowCreatePage = lazy(() => import('./pages/workflows/create'));
const InvoicesPage = lazy(() => import('./pages/invoices'));
const InvoicesViewPage = lazy(() => import('./pages/invoices/[id].tsx'));
const MembershipPage = lazy(() => import('./pages/membership'));
const QuotesPage = lazy(() => import('./pages/quotes'));
const QuotesViewPage = lazy(() => import('./pages/quotes/[id].tsx'));
const WebviewQuote = lazy(() => import('./pages/quotes/webview'));
const CompanyProfilePage = lazy(() => import('./pages/company-profile'));
const CreateProductFormPage = lazy(() => import('./pages/create-product-form'));
const MyProductsPage = lazy(() => import('./pages/my-products'));
const OffPlatformPoliciesListView = lazy(
  () => import('./pages/off-platform-policies-list-view')
);
const OffPlatformPoliciesDetailsView = lazy(
  () => import('./pages/off-platform-policies-details-view')
);
const CompaniesPage = lazy(() => import('./pages/companies'));
const CompaniesViewPage = lazy(() => import('./pages/companies/[id].tsx'));
const OpportunitiesViewPage = lazy(
  () => import('./pages/opportunity/[id].tsx')
);
const CompanyContractsPage = lazy(
  () => import('./pages/company-contracts/[id].tsx')
);
const CompanyContractsDetailPage = lazy(
  () => import('./pages/company-contracts-detail/[id].tsx')
);
const CallDetailsViewPage = lazy(() => import('./pages/call-details/index'));
const IndividualsPage = lazy(() => import('./pages/individuals'));
const IndividualImportPage = lazy(() => import('./pages/individuals/import'));
const IndividualDetailPage = lazy(() => import('./pages/individuals/[id].tsx'));
const DocumentTemplatesPage = lazy(() => import('./pages/document-templates'));
const CalendarPage = lazy(() => import('./pages/calendar'));
const FlightplanPage = lazy(() => import('./pages/flightplan'));
const FlightplanKybPage = lazy(() => import('./pages/flightplan-kyb'));
const FlightplanSignContractPage = lazy(
  () => import('./pages/flightplan-sign-contract')
);
const IntegrationPage = lazy(() => import('./pages/integration'));
const MailLayout = lazy(() => import('./pages/mail/MailLayout'));
const MailIndexRedirect = lazy(() => import('./pages/mail/MailIndexRedirect'));
const MailFolderPage = lazy(() => import('./pages/mail/MailFolderPage'));
const MailLegacyFolderRedirect = lazy(
  () => import('./pages/mail/MailLegacyFolderRedirect')
);
const RecentMessagesPage = lazy(() => import('./pages/email/recent-messages'));
const SendEmailPage = lazy(() => import('./pages/email/send'));
const TodaysEventsPage = lazy(() => import('./pages/email/todays-events'));
const TeamsPage = lazy(() => import('./pages/teams'));
const MarketNewsViewPage = lazy(() => import('./pages/market-news/[id].tsx'));
const MarketNewsListPage = lazy(() => import('./pages/market-news-list'));
const RolesAndPermissionsPage = lazy(
  () => import('./pages/roles-and-permissions')
);
const RoleUsersPage = lazy(
  () => import('./pages/roles-and-permissions/role-users')
);
const RolePermissionsPage = lazy(
  () => import('./pages/role-permissions/roles-permissions')
);
const RequestIllustrationPage = lazy(
  () => import('./pages/request-illustration')
);
const OffshoreApplicationEntrypointPage = lazy(
  () => import('./pages/offshore-application-1-entrypoint')
);
const OffshoreApplicationEntryTypePage = lazy(
  () => import('./pages/offshore-application-2-entrytype')
);
const OffshoreApplicationStepsPage = lazy(
  () => import('./pages/offshore-application-3-steps')
);
const OffshoreClientAccountsPage = lazy(
  () => import('./pages/offshore-client-accounts')
);
const SuccessState = lazy(() => import('./new-components/common/SuccessState'));

const FindProductsPage = lazy(() => import('./pages/find-products'));
const FindApisPage = lazy(() => import('./pages/find-apis'));
const ProductsViewPage = lazy(() => import('./pages/products/[id].tsx'));
const ProductsPage = lazy(() => import('./pages/products'));

const MyProductsStorePage = lazy(() => import('./pages/my-products-store'));
const MyProductsOfflineSolutionsPage = lazy(
  () => import('./pages/my-products-offline-solutions')
);
const CompareProductsPage = lazy(() => import('./pages/compare-products'));

const UserSetupPage = lazy(() => import('./pages/auth/UserSetupJourney'));

const KybVerificationPage = lazy(() => import('./pages/kyb-verification'));
const WingmanPage = lazy(() => import('./pages/wingman'));
const PipelineDealsPage = lazy(() => import('./pages/pipelines/deals'));
const PipelineLeadsPage = lazy(() => import('./pages/pipelines/leads'));
const PipelineLeaderboardPage = lazy(
  () => import('./pages/pipelines/leaderboard')
);
const PipelineLeaderboardTvPage = lazy(
  () => import('./pages/pipelines/leaderboard-tv')
);
const ProjectsPage = lazy(() => import('./pages/projects'));
const ProjectTasksPage = lazy(() => import('./pages/projects/tasks'));

function App() {
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <StoreProvider>
        <ThemeProvider theme={{ baseTheme: 'flightdeck' }}>
          <ModalProvider>
            <DrawerProvider>
              <RouteDataProvider>
            <BrowserRouter
              future={{
                // CRM-608: v7_startTransition defers route commits on BrowserRouter.
                // Heavy Pipelines/Deals pages can stall that transition so the URL
                // updates while the previous page stays mounted.
                v7_relativeSplatPath: true
              }}
            >
              <ApiClientContext.Provider value={api}>
                <TwilioProvider>
                  <AppShellSuspense>
                    <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/logout" element={<LogoutPage />} />
                      <Route
                        path="/:companyUuid/login"
                        element={<LoginPage />}
                      />
                      <Route
                        path="cookie-policy"
                        element={<CookiePolicyPage />}
                      />
                      {/* Call Details routes */}
                      {callDetailsRoutes.map((route) => (
                        <Route
                          key={route.routePath}
                          path={route.routePath}
                          element={
                            <Protected
                              element={
                                <CallDetailsViewPage
                                  returnPath={route.returnPath}
                                  returnLabel={route.returnLabel}
                                />
                              }
                              permissions={
                                route.permissions as PermissionType[]
                              }
                            />
                          }
                        />
                      ))}
                      <Route
                        path="/register"
                        element={
                          <ExternalRedirect to="https://www.stubbenedge.com/" />
                        }
                      />
                      <Route
                        path="/user-setup/:uuid/:token"
                        element={<UserSetupPage />}
                      />
                      <Route
                        path="/kyb-verification/onfido/:uuid/:token"
                        element={<KybVerificationPage />}
                      />
                      <Route path="flightplan" element={<FlightplanPage />} />
                      <Route
                        path="flightplan/kyb"
                        element={<FlightplanKybPage />}
                      />
                      <Route
                        path="flightplan/sign-contract"
                        element={<FlightplanSignContractPage />}
                      />
                      <Route path="dashboard" element={<DashboardPage />} />
                      <Route
                        path="objectives"
                        element={
                          <Protected
                            element={<ObjectivesPage />}
                            permissions={['objective_view']}
                          />
                        }
                      />
                      <Route
                        path="wingman"
                        element={
                          <Protected
                            element={<WingmanPage />}
                            permissions={['chat_with_data']}
                          />
                        }
                      />
                      <Route
                        path="deals"
                        element={<Navigate to="/pipelines" replace />}
                      />
                      <Route path="campaigns" element={<CampaignsPage />} />
                      <Route
                        path="campaigns/:id"
                        element={<CampaignViewPage />}
                      />
                      <Route path="templates" element={<TemplatesPage />} />
                      <Route
                        path="projects/tasks"
                        element={
                          <Protected
                            element={<ProjectTasksPage />}
                            permissions={['task_view', 'task_manage']}
                          />
                        }
                      />
                      <Route
                        path="projects"
                        element={
                          <Protected
                            element={<ProjectsPage />}
                            permissions={[
                              'pipeline_view',
                              'pipeline_access',
                              'pipeline_manage'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="project/:id"
                        element={
                          <Protected
                            element={
                              <PipelineProvider>
                                <PipelineViewPage />
                              </PipelineProvider>
                            }
                            permissions={[
                              'pipeline_access',
                              'pipeline_manage',
                              'pipeline_view'
                            ]}
                          />
                        }
                      />
                      <Route path="integration" element={<IntegrationPage />} />
                      <Route path="mail" element={<MailLayout />}>
                        <Route index element={<MailIndexRedirect />} />
                        <Route
                          path="folder/:folderId"
                          element={<MailLegacyFolderRedirect />}
                        />
                        <Route
                          path=":folderSlug/:folderId"
                          element={<MailFolderPage />}
                        />
                      </Route>
                      <Route
                        path="email/recent-messages"
                        element={<RecentMessagesPage />}
                      />
                      <Route path="email/send" element={<SendEmailPage />} />
                      <Route
                        path="email/todays-events"
                        element={<TodaysEventsPage />}
                      />
                      <Route
                        path="team"
                        element={
                          <Protected
                            element={<TeamsPage />}
                            permissions={[
                              'team_view',
                              'team_create',
                              'team_manage'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="individuals"
                        element={
                          <Protected
                            element={<IndividualsPage />}
                            permissions={[
                              'individual_manage',
                              'individual_view'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="individuals/import"
                        element={
                          <Protected
                            element={<IndividualImportPage />}
                            permissions={['individual_manage']}
                          />
                        }
                      />
                      <Route
                        path="individual/:id"
                        element={
                          <Protected
                            element={<IndividualDetailPage />}
                            permissions={[
                              'individual_manage',
                              'individual_view'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="roles"
                        element={
                          <Protected
                            element={<RolesAndPermissionsPage />}
                            permissions={['role_view', 'role_manage']}
                          />
                        }
                      />
                      <Route
                        path="roles/:id/users"
                        element={
                          <Protected
                            element={<RoleUsersPage />}
                            permissions={['role_view', 'role_manage']}
                          />
                        }
                      />
                      <Route
                        path="role-permissions"
                        element={
                          <Protected
                            element={<RolePermissionsPage />}
                            permissions={['role_manage']}
                          />
                        }
                      />
                      <Route
                        path="role-permissions/:id"
                        element={
                          <Protected
                            element={<RolePermissionsPage />}
                            permissions={['role_manage']}
                          />
                        }
                      />
                      <Route
                        path="companies"
                        element={
                          <Protected
                            element={<CompaniesPage />}
                            permissions={[
                              'company_view',
                              'company_create',
                              'company_manage',
                              'company_profile_edit',
                              'company_profile_view',
                              'company_profile_manage'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="company/:id"
                        element={
                          <Protected
                            element={<CompaniesViewPage />}
                            permissions={[
                              'company_view',
                              'company_create',
                              'company_manage',
                              'company_profile_edit',
                              'company_profile_view',
                              'company_profile_manage'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="opportunity/:id"
                        element={
                          <Protected
                            element={<OpportunitiesViewPage />}
                            permissions={[
                              'company_view',
                              'company_create',
                              'company_manage',
                              'company_profile_edit',
                              'company_profile_view',
                              'company_profile_manage'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="company-contracts/:id"
                        element={
                          <Protected
                            element={<CompanyContractsPage />}
                            permissions={[
                              'company_view',
                              'company_create',
                              'company_manage'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="company-contracts-detail/:id"
                        element={
                          <Protected
                            element={<CompanyContractsDetailPage />}
                            permissions={[
                              'company_view',
                              'company_create',
                              'company_manage'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="company"
                        element={
                          <Protected
                            element={<CompanyProfilePage />}
                            permissions={[
                              'company_view',
                              'company_create',
                              'company_manage',
                              'company_profile_edit',
                              'company_profile_view',
                              'company_profile_manage'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="market-news"
                        element={
                          <Protected
                            element={<MarketNewsPage />}
                            permissions={['news_view', 'news_manage']}
                          />
                        }
                      />
                      <Route
                        path="request-illustration/:id"
                        element={
                          <Protected
                            element={<RequestIllustrationPage />}
                            permissions={['product_view', 'product_manage']}
                          />
                        }
                      />
                      <Route
                        path="offshore-application-success"
                        element={
                          <SuccessState
                            message="The offshore application has been submitted successfully."
                            // title="Application submitted successfully"
                          />
                        }
                      />
                      <Route
                        path="offshore-application-entrypoint/:id"
                        element={
                          <Protected
                            element={<OffshoreApplicationEntrypointPage />}
                            permissions={['product_view', 'product_manage']}
                          />
                        }
                      />
                      <Route
                        path="offshore-application-entrytype/:id"
                        element={
                          <Protected
                            element={<OffshoreApplicationEntryTypePage />}
                            permissions={['product_view', 'product_manage']}
                          />
                        }
                      />
                      <Route
                        path="offshore-application-steps/:id/:type"
                        element={
                          <Protected
                            element={<OffshoreApplicationStepsPage />}
                            permissions={['product_view', 'product_manage']}
                          />
                        }
                      />
                      <Route
                        path="market-news/:id"
                        element={
                          <Protected
                            element={<MarketNewsViewPage />}
                            permissions={['news_view', 'news_manage']}
                          />
                        }
                      />
                      <Route
                        path="market-news-list"
                        element={
                          <Protected
                            element={<MarketNewsListPage />}
                            permissions={['news_view', 'news_manage']}
                          />
                        }
                      />
                      <Route
                        path="find-products"
                        element={
                          <Protected
                            element={<FindProductsPage />}
                            permissions={['product_view', 'product_manage']}
                          />
                        }
                      />
                      <Route
                        path="find-apis"
                        element={
                          <Protected
                            element={<FindApisPage />}
                            permissions={['api_view']}
                          />
                        }
                      />
                      <Route
                        path="providers"
                        element={
                          <Protected
                            element={<ProvidersPage />}
                            permissions={['provider_manage']}
                          />
                        }
                      />
                      <Route
                        path="products"
                        element={
                          <Protected
                            element={<ProductsPage />}
                            permissions={['product_view', 'product_manage']}
                          />
                        }
                      />
                      <Route
                        path="products/:id"
                        element={
                          <Protected
                            element={<ProductsViewPage path="products" />}
                            permissions={['product_view', 'product_manage']}
                          />
                        }
                      />
                      <Route
                        path="my-products/:id"
                        element={
                          <Protected
                            element={<ProductsViewPage path="my-products" />}
                            permissions={['product_view', 'product_manage']}
                          />
                        }
                      />
                      <Route
                        path="compare-products"
                        element={
                          <Protected
                            element={<CompareProductsPage />}
                            permissions={['product_view', 'product_manage']}
                          />
                        }
                      />
                      <Route
                        path="my-products"
                        element={
                          <Protected
                            element={<MyProductsPage />}
                            permissions={['product_view', 'product_manage']}
                          />
                        }
                      />

                      <Route
                        path="my-products-store"
                        element={
                          <Protected
                            element={<MyProductsStorePage />}
                            permissions={['product_view', 'product_manage']}
                          />
                        }
                      />
                      <Route
                        path="my-products/services-other"
                        element={
                          <Protected
                            element={<MyProductsOfflineSolutionsPage />}
                            permissions={['services_other_products_manage']}
                          />
                        }
                      />
                      <Route
                        path="fee-structures"
                        element={
                          <Protected
                            element={<FeeStructuresPage />}
                            permissions={['fee_structure_manage']}
                          />
                        }
                      />
                      <Route
                        path="audiences/upload"
                        element={
                          <Protected
                            element={<AudiencesUploadPage />}
                            permissions={['list_view', 'list_manage']}
                          />
                        }
                      />
                      <Route
                        path="audiences/builder"
                        element={
                          <Protected
                            element={<AudiencesBuilderPage />}
                            permissions={['list_view', 'list_manage']}
                          />
                        }
                      />
                      <Route
                        path="audiences/manual-builder"
                        element={
                          <Protected
                            element={<AudiencesManualBuilderPage />}
                            permissions={['list_view', 'list_manage']}
                          />
                        }
                      />
                      <Route
                        path="audiences/presets"
                        element={
                          <FeatureProtected
                            flag="audience_presets"
                            element={<AudiencesPresetsPage />}
                            permissions={['list_view', 'list_manage']}
                          />
                        }
                      />
                      <Route
                        path="audiences/presets/:presetId"
                        element={
                          <FeatureProtected
                            flag="audience_presets"
                            element={<AudiencesPresetDetailPage />}
                            permissions={['list_view', 'list_manage']}
                          />
                        }
                      />
                      <Route
                        path="audiences"
                        element={
                          <Protected
                            element={<AudiencesPage />}
                            permissions={['list_view', 'list_manage']}
                          />
                        }
                      />
                      <Route
                        path="audiences/archived"
                        element={
                          <Protected
                            element={<AudiencesArchivedPage />}
                            permissions={['list_view', 'list_manage']}
                          />
                        }
                      />
                      <Route
                        path="audiences/:id"
                        element={
                          <Protected
                            element={<AudiencesViewPage />}
                            permissions={['list_view', 'list_manage']}
                          />
                        }
                      />
                      <Route
                        path="fee-structures/:categoryName/:id"
                        element={
                          <Protected
                            element={<FeeStructureDetailPage />}
                            permissions={['fee_structure_manage']}
                          />
                        }
                      />
                      <Route
                        path="create-product-form/:id?"
                        element={
                          <Protected
                            element={<CreateProductFormPage />}
                            permissions={['product_manage']}
                          />
                        }
                      />
                      <Route path="akoni">
                        <Route
                          index
                          element={
                            <SavingsProtected
                              element={<SavingsHomeDashboardPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="individuals"
                          element={
                            <SavingsProtected
                              element={<AkoniIndividualsPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="individuals/:id"
                          element={
                            <SavingsProtected
                              element={<AkoniIndividualDetailPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="customers"
                          element={
                            <SavingsProtected
                              element={<AkoniCustomersPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="customers/:customerId"
                          element={
                            <SavingsProtected
                              element={<AkoniCustomerDetailPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="customers/:customerId/accounts/:accountId"
                          element={
                            <SavingsProtected
                              element={<AkoniCustomerAccountDetailPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="customers/:customerId/hub-transactions"
                          element={
                            <SavingsProtected
                              element={<AkoniCustomerHubTransactionsPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="applications"
                          element={
                            <SavingsProtected
                              element={<AkoniApplicationsPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="applications/:id"
                          element={
                            <SavingsProtected
                              element={<AkoniApplicationDetailPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="deposit-instructions"
                          element={
                            <SavingsProtected
                              element={<AkoniDepositInstructionsPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="deposit-instructions/:id"
                          element={
                            <SavingsProtected
                              element={<AkoniDepositInstructionDetailPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="introducers"
                          element={
                            <SavingsProtected
                              element={<AkoniIntroducersPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="deposits"
                          element={
                            <SavingsProtected
                              element={<AkoniDepositsPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="deposits/:id"
                          element={
                            <SavingsProtected
                              element={<AkoniDepositDetailPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="transactions"
                          element={
                            <SavingsProtected
                              element={<AkoniTransactionsPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="transactions/:id"
                          element={
                            <SavingsProtected
                              element={<AkoniTransactionDetailPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="products"
                          element={
                            <SavingsProtected
                              element={<AkoniProductsPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="products/:id"
                          element={
                            <SavingsProtected
                              element={<AkoniProductDetailPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="banks"
                          element={
                            <SavingsProtected
                              element={<AkoniBanksPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="banks/:id/deposit-instructions"
                          element={
                            <SavingsProtected
                              element={<AkoniBankDepositInstructionsPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="banks/:id"
                          element={
                            <SavingsProtected
                              element={<AkoniBankDetailPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                        <Route
                          path="direct-billing-reports"
                          element={
                            <SavingsProtected
                              element={<AkoniDirectBillingReportsPage />}
                              permissions={['savings_view', 'savings_manage']}
                            />
                          }
                        />
                      </Route>
                      <Route
                        path="flightdeck-passport"
                        element={
                          <Protected
                            element={<MembershipPage />}
                            permissions={allowAllPermissions}
                          />
                        }
                      />
                      <Route path="workflows">
                        <Route index element={<WorkflowsPage />} />
                        <Route path="create" element={<WorkflowCreatePage />} />
                        <Route
                          path=":workflowId"
                          element={<WorkflowEditPage />}
                        />
                      </Route>
                      <Route
                        path="datatable-showcase"
                        element={<DatatableShowcase />}
                      />
                      <Route
                        path="market-valuation"
                        element={
                          <Protected
                            element={<MarketValuationPage />}
                            permissions={allowAllPermissions}
                          />
                        }
                      />
                      <Route
                        path="quotes"
                        element={
                          <Protected
                            element={<QuotesPage />}
                            permissions={['quote_view', 'quote_manage']}
                          />
                        }
                      />
                      <Route
                        path="quotes/:id"
                        element={
                          <Protected
                            element={<QuotesViewPage />}
                            permissions={['quote_view']}
                          />
                        }
                      />
                      <Route
                        path="simple-quotes/:id/summary"
                        element={
                          <Protected
                            element={<WebviewQuote />}
                            permissions={['quote_view']}
                          />
                        }
                      />
                      <Route
                        path="invoices"
                        element={
                          <Protected
                            element={<InvoicesPage />}
                            permissions={[
                              'invoice_view',
                              'invoice_manage',
                              'xero_invoices_view',
                              'xero_invoices_manage'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="invoices/:id"
                        element={
                          <Protected
                            element={<InvoicesViewPage />}
                            permissions={['invoice_view', 'xero_invoices_view']}
                          />
                        }
                      />
                      <Route
                        path="simple-invoices/:id/summary"
                        element={
                          <Protected
                            element={<WebviewInvoice />}
                            permissions={['invoice_view']}
                          />
                        }
                      />
                      <Route
                        path="xero-invoices/:id/summary"
                        element={
                          <Protected
                            element={<WebviewInvoice />}
                            permissions={['xero_invoices_view']}
                          />
                        }
                      />
                      <Route
                        path="data-playpen"
                        element={
                          <Protected
                            element={<DataPlaypenPage />}
                            permissions={allowAllPermissions}
                          />
                        }
                      />
                      <Route
                        path="data-completeness-score"
                        element={<DataCompletenessScorePage />}
                      />
                      <Route
                        path="/bdx-import"
                        element={
                          <Protected
                            element={<BdxImport />}
                            permissions={['policy_view', 'policy_manage']}
                          />
                        }
                      />
                      <Route
                        path="policies"
                        element={
                          <Protected
                            element={<PoliciesPage />}
                            permissions={['policy_view', 'policy_manage']}
                          />
                        }
                      />
                      <Route
                        path="policies/:id"
                        element={
                          <Protected
                            element={<PoliciesViewPage />}
                            permissions={['policy_view', 'policy_manage']}
                          />
                        }
                      />
                      <Route
                        path="/offshore-client-accounts"
                        element={
                          <Protected
                            element={<OffshoreClientAccountsPage />}
                            permissions={[
                              'product_view',
                              'product_request',
                              'product_create',
                              'product_delete',
                              'product_request'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="off-platform-policies"
                        element={
                          <Protected
                            element={<OffPlatformPoliciesListView />}
                            permissions={['off_platform_policy_view']}
                          />
                        }
                      />
                      <Route
                        path="off-platform-policies/:policyId"
                        element={
                          <Protected
                            element={<OffPlatformPoliciesDetailsView />}
                            permissions={['off_platform_policy_view']}
                          />
                        }
                      />
                      <Route
                        path="tasks"
                        element={
                          <Navigate to="/projects/tasks" replace />
                        }
                      />
                      <Route
                        path="tasks/:taskId"
                        element={
                          <Protected
                            element={<TasksViewPage />}
                            permissions={['task_view', 'task_manage']}
                          />
                        }
                      />
                      <Route path="workflows">
                        <Route index element={<WorkflowsPage />} />
                        <Route path="create" element={<WorkflowCreatePage />} />
                        <Route
                          path=":workflowId"
                          element={<WorkflowEditPage />}
                        />
                      </Route>
                      <Route
                        path="pipelines"
                        element={
                          <Protected
                            element={<PipelinesPage />}
                            permissions={[
                              'pipeline_access',
                              'pipeline_manage',
                              'pipeline_view'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="pipelines/deals"
                        element={
                          <Protected
                            element={<PipelineDealsPage />}
                            permissions={[
                              'pipeline_access',
                              'pipeline_manage',
                              'pipeline_view'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="pipelines/leads"
                        element={
                          <Protected
                            element={<PipelineLeadsPage />}
                            permissions={[
                              'pipeline_access',
                              'pipeline_manage',
                              'pipeline_view'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="pipelines/leaderboard/tv"
                        element={
                          <Protected
                            element={<PipelineLeaderboardTvPage />}
                            permissions={[
                              'pipeline_access',
                              'pipeline_manage',
                              'pipeline_view'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="pipelines/leaderboard"
                        element={
                          <Protected
                            element={<PipelineLeaderboardPage />}
                            permissions={[
                              'pipeline_access',
                              'pipeline_manage',
                              'pipeline_view'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="pipeline/:id"
                        element={
                          <Protected
                            element={
                              <PipelineProvider>
                                <PipelineViewPage />
                              </PipelineProvider>
                            }
                            permissions={[
                              'pipeline_access',
                              'pipeline_manage',
                              'pipeline_view'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="pipeline/:id/import"
                        element={
                          <Protected
                            element={<PipelineDealImportPage />}
                            permissions={['task_manage']}
                          />
                        }
                      />
                      <Route
                        path="pipeline/:id/tasks/import"
                        element={
                          <Protected
                            element={<PipelineTaskImportPage />}
                            permissions={['task_manage']}
                          />
                        }
                      />
                      <Route
                        path="my-pipeline/:id"
                        element={
                          <Protected
                            element={
                              <PipelineProvider>
                                <PipelineViewPage />
                              </PipelineProvider>
                            }
                            permissions={[
                              'pipeline_access',
                              'pipeline_manage',
                              'pipeline_view'
                            ]}
                          />
                        }
                      />
                      <Route
                        path="calendar"
                        element={
                          <Protected
                            element={<CalendarPage />}
                            permissions={['events_view', 'team_events_view']}
                          />
                        }
                      />
                      <Route path="*" element={<NotFoundPage />} />
                      <Route
                        path="document-templates"
                        element={
                          <Protected
                            element={<DocumentTemplatesPage />}
                            permissions={[
                              'document_template_view',
                              'document_template_manage'
                            ]}
                          />
                        }
                      />
                    </Routes>
                  </AppShellSuspense>
                </TwilioProvider>
              </ApiClientContext.Provider>
            </BrowserRouter>
              </RouteDataProvider>
            </DrawerProvider>
          </ModalProvider>
        </ThemeProvider>
      </StoreProvider>
    </QueryClientProvider>
  );
}

export default App;
